import React, { useState } from "react";
import { FileText, Loader2, Send, CheckCircle2, Undo2, Pencil, Bell, Wallet, Receipt, Ban, FileCheck2, XCircle, Handshake, ScrollText } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { billing, openAuthed, fmtMoney } from "@/lib/billingApi";
import { useBT } from "@/i18n/billingText";

const btn = "h-7 px-2 text-xs border-slate-700 bg-slate-900 hover:bg-slate-800";

// Small form dialog: fields = [{ key, label, type, default, options }]
export const AskDialog = ({ ask, onClose }) => {
  const { bt } = useBT();
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState(false);
  if (!ask) return null;
  const v = (f) => (vals[f.key] !== undefined ? vals[f.key] : f.default ?? "");
  const submit = async () => {
    setBusy(true);
    try {
      const out = {};
      ask.fields.forEach((f) => { out[f.key] = v(f); });
      await ask.run(out);
      setVals({});
      onClose(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.toString?.() || "Error");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent className="bg-slate-900 border-slate-700 text-slate-100" data-testid="billing-ask">
        <DialogHeader><DialogTitle>{ask.title}</DialogTitle></DialogHeader>
        {ask.text && <p className="text-sm text-slate-400">{ask.text}</p>}
        <div className="space-y-3">
          {ask.fields.map((f) => (
            <label key={f.key} className="block text-xs text-slate-400">{f.label}
              {f.type === "select" ? (
                <select className="mt-1 h-9 w-full rounded-md border border-slate-700 bg-slate-950 px-2 text-sm" value={v(f)} onChange={(e) => setVals({ ...vals, [f.key]: e.target.value })}>
                  {f.options.map(([val, lab]) => <option key={val} value={val}>{lab}</option>)}
                </select>
              ) : f.type === "file" ? (
                <input type="file" accept=".pdf,image/*" className="mt-1 block w-full text-sm" onChange={(e) => setVals({ ...vals, [f.key]: e.target.files?.[0] })} />
              ) : (
                <Input type={f.type || "text"} className="mt-1 bg-slate-950 border-slate-700" value={v(f)} onChange={(e) => setVals({ ...vals, [f.key]: e.target.value })} data-testid={`ask-${f.key}`} />
              )}
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onClose(false)}>{bt("cancel")}</Button>
          <Button className={ask.danger ? "bg-red-500 hover:bg-red-400 text-white" : "bg-venka-green text-slate-950 hover:bg-venka-green-light"} disabled={busy} onClick={submit} data-testid="ask-confirm">
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{ask.cta}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const pdfName = (doc) => `${(doc.number || "draft").replace(/[ /]/g, "_")}.pdf`;

export const ProposalActions = ({ p, onChange, onEdit }) => {
  const { bt } = useBT();
  const { isAdmin } = useAuth();
  const [busy, setBusy] = useState(false);
  const [ask, setAsk] = useState(null);
  const act = async (action, body, msg) => {
    setBusy(true);
    try {
      await billing.proposalAction(p.id, action, body);
      if (msg) toast.success(msg);
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.toString?.() || "Error");
    } finally {
      setBusy(false);
    }
  };
  const approveSend = async () => {
    setBusy(true);
    try {
      await billing.proposalAction(p.id, "approve");
      await billing.proposalAction(p.id, "send");
      toast.success(bt("st.sent"));
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.toString?.() || "Error");
    } finally {
      setBusy(false);
    }
  };
  const s = p.status;
  return (
    <div className="flex flex-wrap gap-1.5">
      <Button size="sm" variant="outline" className={btn} onClick={() => openAuthed(`/billing/proposals/${p.id}/pdf`)} data-testid="proposal-pdf"><FileText className="h-3 w-3 mr-1" />{bt("pdf")}</Button>
      {p.contract_id && (
        <Button size="sm" variant="outline" className={btn} onClick={() => openAuthed(`/billing/proposals/${p.id}/contract.pdf`)} data-testid="proposal-contract"><ScrollText className="h-3 w-3 mr-1" />{bt("contract")} {p.contract_number}</Button>
      )}
      {s === "accepted" && !p.contract_id && (
        <Button size="sm" variant="outline" className={btn} disabled={busy} onClick={() => act("contract", {}, bt("contract"))} data-testid="proposal-contract-issue"><ScrollText className="h-3 w-3 mr-1" />{bt("issueContract")}</Button>
      )}
      {["draft", "pending_approval", "approved"].includes(s) && onEdit && (
        <Button size="sm" variant="outline" className={btn} onClick={() => onEdit(p)} data-testid="proposal-edit"><Pencil className="h-3 w-3 mr-1" />{bt("edit")}</Button>
      )}
      {s === "draft" && !isAdmin && (
        <Button size="sm" variant="outline" className={btn} disabled={busy} onClick={() => act("submit", {}, bt("st.pending_approval"))} data-testid="proposal-submit"><Send className="h-3 w-3 mr-1" />{bt("submitApproval")}</Button>
      )}
      {isAdmin && ["draft", "pending_approval"].includes(s) && (
        <>
          <Button size="sm" className="h-7 px-2 text-xs bg-venka-green text-slate-950 hover:bg-venka-green-light" disabled={busy} onClick={approveSend} data-testid="proposal-approve-send"><CheckCircle2 className="h-3 w-3 mr-1" />{bt("approveSend")}</Button>
          <Button size="sm" variant="outline" className={btn} disabled={busy} onClick={() => act("approve", {}, bt("st.approved"))} data-testid="proposal-approve">{bt("approve")}</Button>
        </>
      )}
      {isAdmin && ["pending_approval", "approved"].includes(s) && (
        <Button size="sm" variant="outline" className={btn} onClick={() => setAsk({ title: bt("returnDraft"), cta: bt("returnDraft"), fields: [{ key: "reason", label: bt("reason") }], run: (v) => billing.proposalAction(p.id, "reject", v) })}><Undo2 className="h-3 w-3 mr-1" />{bt("returnDraft")}</Button>
      )}
      {s === "approved" && (
        <Button size="sm" className="h-7 px-2 text-xs bg-venka-green text-slate-950 hover:bg-venka-green-light" disabled={busy} onClick={() => act("send", {}, bt("st.sent"))} data-testid="proposal-send"><Send className="h-3 w-3 mr-1" />{bt("sendCustomer")}</Button>
      )}
      {["approved", "sent"].includes(s) && (
        <Button size="sm" variant="outline" className={btn} onClick={() => setAsk({ title: bt("acceptOnBehalf"), cta: bt("accept"), fields: [{ key: "note", label: bt("notes") }], run: (v) => billing.proposalAction(p.id, "accept-on-behalf", v) })} data-testid="proposal-accept-behalf"><Handshake className="h-3 w-3 mr-1" />{bt("acceptOnBehalf")}</Button>
      )}
      {!["accepted", "cancelled", "declined"].includes(s) && (
        <Button size="sm" variant="outline" className={`${btn} text-red-300`} disabled={busy} onClick={() => act("cancel")}><Ban className="h-3 w-3" /></Button>
      )}
      <AskDialog ask={ask} onClose={(ok) => { setAsk(null); if (ok) onChange?.(); }} />
    </div>
  );
};

export const InvoiceActions = ({ inv, onChange }) => {
  const { bt } = useBT();
  const { isAdmin } = useAuth();
  const [ask, setAsk] = useState(null);
  const openDoc = ["open", "partially_paid"].includes(inv.status);
  const remind = async () => {
    try {
      await billing.invoiceAction(inv.id, "remind");
      toast.success(bt("remind"));
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.toString?.() || "Error");
    }
  };
  return (
    <div className="flex flex-wrap gap-1.5">
      <Button size="sm" variant="outline" className={btn} onClick={() => openAuthed(`/billing/invoices/${inv.id}/pdf`)} data-testid="invoice-pdf"><FileText className="h-3 w-3 mr-1" />{bt("pdf")}</Button>
      {openDoc && (
        <>
          <Button size="sm" className="h-7 px-2 text-xs bg-venka-green text-slate-950 hover:bg-venka-green-light" data-testid="invoice-record-payment"
            onClick={() => setAsk({
              title: `${bt("recordPayment")} · ${inv.number}`, cta: bt("recordPayment"),
              fields: [
                { key: "amount", label: `${bt("amount")} (${inv.currency})`, type: "number", default: inv.balance },
                { key: "method", label: bt("method"), type: "select", default: "bank_transfer", options: ["bank_transfer", "cash", "card", "other"].map((m) => [m, bt(`methods.${m}`)]) },
                { key: "reference", label: bt("reference") },
              ],
              run: (v) => billing.recordPayment(inv.id, { ...v, amount: Number(v.amount) }),
            })}><Wallet className="h-3 w-3 mr-1" />{bt("recordPayment")}</Button>
          <Button size="sm" variant="outline" className={btn} onClick={remind} data-testid="invoice-remind"><Bell className="h-3 w-3 mr-1" />{bt("remind")}</Button>
        </>
      )}
      {inv.kind === "proforma" && inv.status !== "cancelled" && (
        <Button size="sm" variant="outline" className={btn} onClick={() => setAsk({
          title: bt("fiscal"), cta: bt("save"),
          fields: [{ key: "number", label: bt("fiscalNumber"), default: inv.fiscal_invoice_number || "" }, { key: "file", label: "PDF", type: "file" }],
          run: (v) => billing.recordFiscal(inv.id, v.number, v.file),
        })} data-testid="invoice-fiscal"><FileCheck2 className="h-3 w-3 mr-1" />{bt("fiscal")}</Button>
      )}
      {isAdmin && inv.kind === "invoice" && !["credited", "cancelled"].includes(inv.status) && (
        <Button size="sm" variant="outline" className={btn} onClick={() => setAsk({
          title: `${bt("creditNote")} · ${inv.number}`, cta: bt("creditNote"), danger: true,
          fields: [{ key: "amount", label: `${bt("amount")} (${inv.currency})`, type: "number", default: inv.totals?.total - (inv.credited_amount || 0) }, { key: "reason", label: bt("reason") }],
          run: (v) => billing.invoiceAction(inv.id, "credit-note", { ...v, amount: Number(v.amount) }),
        })}><Receipt className="h-3 w-3 mr-1" />{bt("creditNote")}</Button>
      )}
      {isAdmin && inv.kind === "proforma" && openDoc && !inv.amount_paid && (
        <Button size="sm" variant="outline" className={`${btn} text-red-300`} onClick={() => setAsk({
          title: `${bt("cancelDoc")} · ${inv.number}`, cta: bt("cancelDoc"), danger: true, fields: [{ key: "reason", label: bt("reason") }],
          run: (v) => billing.invoiceAction(inv.id, "cancel", v),
        })}><XCircle className="h-3 w-3 mr-1" />{bt("cancelDoc")}</Button>
      )}
      <AskDialog ask={ask} onClose={(ok) => { setAsk(null); if (ok) onChange?.(); }} />
    </div>
  );
};

export const PaymentVerifyActions = ({ pay, onChange }) => {
  const { bt } = useBT();
  const [ask, setAsk] = useState(null);
  if (pay.status !== "pending_verification") return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      <Button size="sm" className="h-7 px-2 text-xs bg-venka-green text-slate-950 hover:bg-venka-green-light" data-testid="payment-verify"
        onClick={() => setAsk({
          title: `${bt("verify")} · ${pay.invoice_number}`, cta: bt("verify"),
          text: `${bt("amount")}: ${fmtMoney(pay.amount, pay.currency)}`,
          fields: [{ key: "amount", label: bt("verifyAmount"), type: "number", default: pay.amount }],
          run: (v) => billing.verifyPayment(pay.id, { amount: Number(v.amount) }),
        })}><CheckCircle2 className="h-3 w-3 mr-1" />{bt("verify")}</Button>
      <Button size="sm" variant="outline" className={`${btn} text-red-300`} onClick={() => setAsk({
        title: `${bt("reject")} · ${pay.invoice_number}`, cta: bt("reject"), danger: true, fields: [{ key: "reason", label: bt("reason") }],
        run: (v) => billing.rejectPayment(pay.id, v.reason),
      })} data-testid="payment-reject"><XCircle className="h-3 w-3 mr-1" />{bt("reject")}</Button>
      <AskDialog ask={ask} onClose={(ok) => { setAsk(null); if (ok) onChange?.(); }} />
    </div>
  );
};
